import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import {
  SearchState,
  SearchQueryDto,
  SearchResponseDto,
  SuggestionResponseDto,
} from "./types";

// Base URL for the Search Platform Microservice
// Adjust the environment variable name as per your project configuration
const API_BASE_URL =[
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_ECOMMERCE_PERFIX,
]
  .filter(Boolean)
  .join("");

if (!process.env.REACT_APP_API_BASE_URL) {
  console.warn("Warning: REACT_APP_API_BASE_URL is not defined.");
}
if (!process.env.REACT_APP_ECOMMERCE_PERFIX) {
  console.warn(
    "Warning: REACT_APP_ECOMMERCE_PERFIX is not defined.",
  );
}


const initialState: SearchState = {
  searchResults: {
    data: null,
    loading: false,
    error: null,
  },
  suggestions: {
    data: [],
    loading: false,
    error: null,
  },
};

/**
 * Async Thunks
 */

// Search Products
export const searchProducts = createAsyncThunk(
  "search/searchProducts",
  async (query: SearchQueryDto, { rejectWithValue }) => {
    try {
      

      const response = await axios.get<SearchResponseDto>(
        `${API_BASE_URL}/search`,
        { params: query },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to perform search",
      );
    }
  },
);

// Filter Products - POST request but using @Query in backend
export const filterProducts = createAsyncThunk(
  "search/filterProducts",
  async (query: SearchQueryDto, { rejectWithValue }) => {
    try {
     
      const response = await axios.post<SearchResponseDto>(
        `${API_BASE_URL}/search/filter`,
        null,
        { params: query },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to filter products",
      );
    }
  },
);

// Get Suggestions
export const getSuggestions = createAsyncThunk(
  "search/getSuggestions",
  async (q: string, { rejectWithValue }) => {
    try {
     
    const response = await axios.get<SuggestionResponseDto[]>(
        `${API_BASE_URL}/search/suggestions`,
        { params: { q } },
      );
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get suggestions",
      );
    }
  },
);

const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    clearSearchState: (state) => {
      state.searchResults = initialState.searchResults;
      state.suggestions = initialState.suggestions;
    },
    clearSuggestions: (state) => {
      state.suggestions.data = [];
    },
  },
  extraReducers: (builder) => {
    // Search Products
    builder.addCase(searchProducts.pending, (state) => {
      state.searchResults.loading = true;
      state.searchResults.error = null;
    });
    builder.addCase(
      searchProducts.fulfilled,
      (state, action: PayloadAction<SearchResponseDto>) => {
        state.searchResults.loading = false;
        state.searchResults.data = action.payload;
      },
    );
    builder.addCase(searchProducts.rejected, (state, action) => {
      state.searchResults.loading = false;
      state.searchResults.error = action.payload as string;
    });

    // Filter Products
    builder.addCase(filterProducts.pending, (state) => {
      state.searchResults.loading = true;
      state.searchResults.error = null;
    });
    builder.addCase(
      filterProducts.fulfilled,
      (state, action: PayloadAction<SearchResponseDto>) => {
        state.searchResults.loading = false;
        state.searchResults.data = action.payload;
      },
    );
    builder.addCase(filterProducts.rejected, (state, action) => {
      state.searchResults.loading = false;
      state.searchResults.error = action.payload as string;
    });

    // Get Suggestions
    builder.addCase(getSuggestions.pending, (state) => {
      state.suggestions.loading = true;
      state.suggestions.error = null;
    });
    builder.addCase(
      getSuggestions.fulfilled,
      (state, action: PayloadAction<SuggestionResponseDto[]>) => {
        state.suggestions.loading = false;
        state.suggestions.data = action.payload;
      },
    );
    builder.addCase(getSuggestions.rejected, (state, action) => {
      state.suggestions.loading = false;
      state.suggestions.error = action.payload as string;
    });
  },
});

export const { clearSearchState, clearSuggestions } = searchSlice.actions;

export default searchSlice.reducer;
